<template>
  <RevDrawer
    :close-button-label="i18n(translations.cancel)"
    data-qa="cancel-orderline-drawer"
    :name="modalName"
    :title="i18n(translations.title)"
    @open="fetchOrderDetails"
  >
    <template #body>
      <RevLoadingScreen
        v-if="isFetchingOrderDetails"
        :text="i18n(translations.loading)"
        variant="light"
      />

      <template v-else>
        <RevInfoBlock
          v-if="apiError"
          class="mt-16"
          :icon="IconWarning"
          :title="apiError.title"
          variant="danger"
        >
          {{ apiError.detail }}
        </RevInfoBlock>

        <RevForm
          :id="FORM_ID"
          :initial-values="INITIAL_VALUES"
          :validate
          @submit="handleSubmit"
        >
          <template #default="{ values, errors }">
            <p class="body-1-bold my-16">
              {{
                i18n(translations.prompt, {
                  sku: orderLine.sku,
                  orderId: order.id,
                })
              }}
            </p>

            <RevInputTextarea
              id="message"
              v-model="values.message"
              data-qa="cancel-message"
              :error="errors.message"
              :label="i18n(translations.message)"
              :rows="5"
            />
          </template>
        </RevForm>
      </template>
    </template>

    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          data-qa="confirm-cancel-orderline"
          :form="FORM_ID"
          full-width="always"
          :loading="isSubmitting"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.confirm) }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import {
  cancelOrderLine,
  getOrderDetails,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import type { GetSellerXpOrderDetails } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-pages.types'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import type { Order, OrderLine } from '../../../../../../../../Tab.types'

import {
  FORM_ID,
  INITIAL_VALUES,
  MODAL_NAME,
} from './CancelOrderLineDrawer.constants'
import translations from './CancelOrderLineDrawer.translations'

type FormValues = {
  message: string
}

const props = defineProps<{
  order: Order
  orderLine: OrderLine
  refreshOrders: () => void
}>()

const i18n = useI18n()
const { openSuccessToast } = useTheToast()

const apiError = ref<null | HttpApiError>(null)
const isSubmitting = ref(false)
const isFetchingOrderDetails = ref(false)
const modalName = computed(() => MODAL_NAME + props.orderLine.id)
const orderDetails = ref<GetSellerXpOrderDetails | null>(null)

const validate = makeValidate<FormValues>({
  message: required(i18n(translations.required)),
})

async function fetchOrderDetails() {
  try {
    isFetchingOrderDetails.value = true
    orderDetails.value = await $httpFetch(getOrderDetails, {
      pathParams: {
        orderId: props.order.id,
      },
      queryParams: {
        view: 'simple',
      },
    })
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isFetchingOrderDetails.value = false
  }
}

function getAssociatedOrderLine() {
  return orderDetails.value?.orderLines.find(
    (orderLine) => orderLine.sku === props.orderLine.sku,
  )
}

async function handleSubmit({ message }: FormValues) {
  try {
    isSubmitting.value = true
    apiError.value = null

    await $httpFetch(cancelOrderLine, {
      pathParams: {
        orderId: props.order.id,
      },
      body: {
        items: [
          {
            id: getAssociatedOrderLine()?.id,
            reason: message,
          },
        ],
      },
    })

    openSuccessToast({
      content: i18n(translations.successMessage),
    })
    props.refreshOrders()
    closeModal(modalName.value)
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isSubmitting.value = false
  }
}

function reset() {
  apiError.value = null
}

function handleCancel() {
  reset()
  closeModal(modalName.value)
}
</script>
